import { type INode } from '@shared/lib/ris.types'
import { type FunctionComponent } from 'react'
import Paragraph from './Paragraph/Paragraph'
import Fallback from './Fallback'
import List from './List/List'
import Table from './Table/Table'
import { ExternalLink, Paperclip } from 'lucide-react'
import Button from '@shared/components/Button/Button'
import { Split, Stack } from '@christiankaindl/lyts'
import Text from '@shared/components/Text/Text'
import { theme } from '@/styles/theme.css'
import { type NormDocument, normalizeDocument } from '@shared/lib/norms.types'
import Heading from '@shared/components/Heading/Heading'
import type {
  ContentNodeType,
  LawPG,
  ClauseContentPG,
} from '@shared/db/schema/laws'
import Inline from './Inline/Inline'
import * as titleStyles from './Title/Title.css'
import { HoverOptions } from './Title/Title.client'

interface Props {
  clausePG: ClauseContentPG
  totalClauses: number
  renderTitle?: boolean
  law?: LawPG
}

export default function ClauseServer({
  clausePG,
  totalClauses,
  renderTitle = true,
  law,
}: Props) {
  const children = clausePG.children ?? []
  const richRefLinks = totalClauses < 100
  const documents = (clausePG.documents ?? []) as NormDocument[]
  const validDocuments = documents.map(normalizeDocument).filter((doc) => {
    return doc.role !== 'mainDocument' && doc.formats.length > 0
  })

  return (
    <>
      {renderTitle &&
        (!children[0]?.type?.startsWith('ueberschrift') ||
          children[0]?.value == '') && (
          <ClauseTitle
            enumeration={clausePG.enumeration}
            title={clausePG.title}
            counts={clausePG.counts}
            law={law}
            clauseSlug={clausePG.slug}
          />
        )}
      {children.map((child, index) => {
        if (!renderTitle && child.type?.startsWith('ueberschrift')) {
          return null
        }
        return (
          <SwitchTypes
            key={`${clausePG.id}-${index}`}
            child={child as INode}
            index={index}
            richRefLinks={richRefLinks}
            enumeration={clausePG.enumeration}
            counts={clausePG.counts}
            law={law}
            clauseSlug={clausePG.slug}
          />
        )
      })}
      {validDocuments.length > 0 && (
        <Stack
          gap='1px'
          style={{
            borderRadius: 15,
            padding: 15,
            backgroundColor: theme.colors.backgrounds.bgA1,
          }}
        >
          <Text color='secondary' size='small'>
            Anhänge
          </Text>
          {validDocuments.map((doc) => {
            const format = Object.entries(doc.formats).find(
              ([, url]) => url,
            )?.[0]
            const url = format ? doc.formats[format] : undefined
            return (
              <Button
                title='Anhang in neuem Tab öffnen'
                style={{ width: 'auto', marginLeft: -6, marginRight: -6 }}
                variant='flat'
                key={doc.title}
                href={url}
              >
                <Paperclip size={16} />
                {doc.title}
                <Split />
                <span style={{ fontWeight: 'normal', opacity: 0.4 }}>
                  {format?.toUpperCase()}
                </span>
                <ExternalLink size={16} style={{ opacity: 0.4 }} />
              </Button>
            )
          })}
        </Stack>
      )}
    </>
  )
}

function ClauseTitle({
  enumeration,
  title,
  counts,
  law,
  clauseSlug,
  children,
}: {
  enumeration: string
  title?: string | null
  counts?: Record<string, number> | null
  law?: LawPG
  clauseSlug?: string
  children?: React.ReactNode
}) {
  const heading = (
    <Heading h={4}>
      {enumeration}&ensp;{children ?? title}
    </Heading>
  )
  if (!law) {
    return heading
  }
  return (
    <HoverOptions
      enumeration={enumeration}
      law={law}
      counts={counts ?? undefined}
      clauseSlug={clauseSlug}
    >
      {heading}
    </HoverOptions>
  )
}

export const SwitchTypes: FunctionComponent<{
  child: INode | ContentNodeType | string
  index: number
  richRefLinks?: boolean
  enumeration?: string
  counts?: Record<string, number> | null
  law?: LawPG
  clauseSlug?: string
}> = function ({
  child,
  index,
  richRefLinks,
  enumeration,
  counts,
  law,
  clauseSlug,
}) {
  if (typeof child === 'string') {
    return <Paragraph value={child} />
  }
  const node = child as INode
  if (
    node.type?.startsWith('ueberschrift') ||
    node.type === 'h1' ||
    node.type === 'h2' ||
    node.type === 'h3' ||
    node.type === 'h4' ||
    node.type === 'h5' ||
    node.type === 'h6'
  ) {
    const hLevel = (
      node.level != null ? Math.min(Number(node.level) + 1, 6) : 4
    ) as 1 | 2 | 3 | 4 | 5 | 6
    if (
      node.type === 'ueberschrift.para' ||
      node.type === 'ueberschrift.titel'
    ) {
      return (
        <ClauseTitle
          enumeration={enumeration ?? ''}
          counts={counts}
          law={law}
          clauseSlug={clauseSlug}
        >
          {node.value ?? <Inline {...node} />}
        </ClauseTitle>
      )
    }
    return (
      <Heading h={hLevel} className={titleStyles.typeSection}>
        <Inline {...node} />
      </Heading>
    )
  } else if (node.type === 'parties') {
    return <PartiesBlock {...node} />
  } else if (node.type === 'quote') {
    return (
      <QuoteBlock
        {...node}
        index={index}
        richRefLinks={richRefLinks}
        enumeration={enumeration}
      />
    )
  } else if (node.type?.startsWith('absatz') || node.type === 'p') {
    return <Paragraph {...node} richRefLinks={richRefLinks} />
  } else if (node.type?.startsWith('table')) {
    return <Table {...node} />
  } else if (node.type?.startsWith('liste')) {
    return <List {...node} />
  } else if (node.type?.startsWith('abstand')) {
    return null
  } else if (node.type?.startsWith('inhaltsvz')) {
    return null
  } else {
    return <Fallback {...node} />
  }
}

function QuoteBlock(
  props: INode & {
    index: number
    richRefLinks?: boolean
    enumeration?: string
  },
) {
  const { children: quoteChildren, richRefLinks, enumeration } = props
  return (
    <blockquote
      style={{
        borderLeft: '2px solid rgba(0, 0, 0, 0.12)',
        paddingLeft: '1em',
        margin: '0.25em 0',
        fontStyle: 'italic',
      }}
    >
      {quoteChildren?.map((child, i) => (
        <SwitchTypes
          key={i}
          child={child}
          index={i}
          richRefLinks={richRefLinks}
          enumeration={enumeration}
        />
      ))}
    </blockquote>
  )
}

function PartiesBlock(props: INode) {
  const plaintiffs = (props.plaintiffs ?? []) as string[]
  const defendants = (props.defendants ?? []) as string[]
  const separator = (props.separator ?? 'v.') as string

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: '24px 0',
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {plaintiffs.map((p) => (
          <span
            key={p}
            style={{
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: '#1a1a1a',
            }}
          >
            {p}
          </span>
        ))}
      </div>
      <span
        style={{
          fontSize: 12,
          fontWeight: 500,
          fontStyle: 'italic',
          opacity: 0.5,
          padding: '2px 14px',
          borderRadius: 4,
          background: '#f5f5f5',
          margin: '12px 0',
          userSelect: 'none',
        }}
      >
        {separator}
      </span>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        {defendants.map((d) => (
          <span
            key={d}
            style={{
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: '-0.01em',
              color: '#1a1a1a',
            }}
          >
            {d}
          </span>
        ))}
      </div>
    </div>
  )
}
